import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Platform = 'ios' | 'macos';

const STORAGE_KEY = 'pocket-agent-platform';

function readStoredPlatform(): Platform {
  if (typeof window === 'undefined') return 'ios';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'macos' ? 'macos' : 'ios';
}

type PlatformContextValue = {
  platform: Platform;
  setPlatform: (platform: Platform) => void;
};

const PlatformContext = createContext<PlatformContextValue | null>(null);

export function PlatformProvider({ children }: { children: ReactNode }) {
  const [platform, setPlatformState] = useState<Platform>(readStoredPlatform);

  const setPlatform = useCallback((next: Platform) => {
    setPlatformState(next);
    window.localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const value = useMemo(() => ({ platform, setPlatform }), [platform, setPlatform]);

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
}

export function usePlatform() {
  const ctx = useContext(PlatformContext);
  if (!ctx) {
    throw new Error('usePlatform must be used within PlatformProvider');
  }
  return ctx;
}
